import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type { Api } from '~/api/endpoints'
import { clearSession, onSessionCleared, saveSession } from '~/auth/session-storage'
import type { DataService } from '~/data/service'
import type { AccountSettings, DistanceUnits, Plan, Session, Units } from '~/data/types'
import { forgetDiarySteps } from '~/lib/diary-undo'
import { forgetGoalsReached } from '~/lib/goal-reached'
import { regionDefaultDistanceUnits, regionDefaultUnits } from '~/lib/units'

export interface AuthContextValue {
  readonly session: Session | null
  /** The units to show: the account's explicit choice, or the device region's default. */
  readonly units: Units
  /** And the same for distance, answered separately — see the units settings. */
  readonly distanceUnits: DistanceUnits
  /** The settings the account carries, which follow it to every device it signs in on. */
  readonly settings: AccountSettings
  /**
   * What the account's plan allows. Null where the session predates plans and this device has
   * not read the account back yet — nothing is gated on an answer nobody has given.
   */
  readonly plan: Plan | null
  signInWithGoogle: (idToken: string) => Promise<void>
  signOut: () => Promise<void>
  setUnits: (units: Units) => Promise<void>
  setDistanceUnits: (units: DistanceUnits) => Promise<void>
  /** Changes whichever settings were named and leaves the rest as they stand. */
  setSettings: (settings: Partial<AccountSettings>) => Promise<void>
  deleteAccount: () => Promise<void>
}

/**
 * What every setting reads as until the account says otherwise. They stand in twice: with
 * nobody signed in, and — more importantly — under a session stored by a build that predates
 * the settings, where `user.settings` is simply missing. Merging over these rather than
 * trusting the stored object is what keeps that session from turning into a half-built one
 * the moment something is changed.
 */
export const DEFAULT_ACCOUNT_SETTINGS: AccountSettings = {
  showWarmups: true,
  workoutsInDiary: true,
  diarySwipeRight: 'copy',
  diarySwipeLeft: 'delete',
  weighInNudge: true,
  weighInNudgeFrom: 420,
  weighInRoutine: [],
  holdSlowPercent: 50,
  holdFastPercent: 200,
  jumpBackSeconds: 5,
  jumpForwardSeconds: 5,
  shareFaces: 'others',
  shareFacesAsk: true,
  birthDate: null,
  sex: null,
  heightM: null,
  activityLevel: 'moderate',
}

const AuthContext = createContext<AuthContextValue | null>(null)

export interface AuthProviderProps {
  readonly api: Api
  readonly service: DataService
  readonly initialSession: Session | null
  readonly children: ReactNode
}

/** The account's settings, whatever a stored session happens to be missing. */
function settingsOf(user: Session['user']): AccountSettings {
  return { ...DEFAULT_ACCOUNT_SETTINGS, ...user.settings }
}

export function AuthProvider({ api, service, initialSession, children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(initialSession)

  /*
   * Whether anything has been chosen on this device since the load began. The account is read
   * once at mount, and that answer is older than any choice made while it was in flight — so
   * once there is a local choice, the fetched account is the stale one and is dropped.
   */
  const chosenHere = useRef(false)

  /*
   * The API client clears the stored session on a 401; reflect that here — and drop the mirror
   * with it. What the mirror holds belongs to the account that was signed in, cursor and all,
   * so leaving it for whoever signs in next would show them somebody else's rows and then
   * resume that account's cursor, below which their own rows would never arrive.
   */
  useEffect(
    () =>
      onSessionCleared(() => {
        void service.reset()
        setSession(null)
      }),
    [service],
  )

  /*
   * The account as the server has it, read once a load. It is what carries a setting changed
   * on another device to this one — the session is kept between loads, so without this a
   * choice made on the phone would wait for the next sign-in. Nothing waits on it: the page
   * shows what it already knows, and the answer lands when it lands.
   */
  useEffect(() => {
    if (initialSession === null) {
      return
    }
    let alive = true
    void api
      .getMe()
      .then((user) => {
        if (!alive || chosenHere.current) {
          return
        }
        setSession((current) => {
          if (!current) {
            return current
          }
          const next: Session = { ...current, user }
          saveSession(next)
          return next
        })
      })
      .catch(() => {
        // Offline, or the server is unwell: what this device already has still stands.
      })
    return () => {
      alive = false
    }
  }, [api, initialSession])

  const signInWithGoogle = useCallback(
    async (idToken: string) => {
      const next = await api.signInWithGoogle(idToken)
      saveSession(next)
      // Whatever the browser was holding belonged to whoever was signed in before. A sign-in
      // starts from nothing, so no row and no cursor crosses between two accounts.
      await service.reset()
      // Keep the welcome's working control on screen until the signed-in surface has a
      // settled first answer. Otherwise an empty in-memory state briefly claims there are no
      // sets before the first timeline page arrives.
      await service.init()
      await service.refresh()
      setSession(next)
    },
    [api, service],
  )

  const signOut = useCallback(async () => {
    try {
      await api.signOut()
    } catch {
      // A sign-out that never reaches the server still signs this device out.
    }
    clearSession()
    setSession(null)
    // What the diary could undo belonged to whoever was signed in; it goes with them, and so
    // does whether they have been told a goal was reached.
    forgetDiarySteps()
    forgetGoalsReached()
    // The mirror belongs to the account that filled it, so it goes with the session: the
    // next account to sign in on this tab must never open on the last one's rows.
    await service.reset()
  }, [api, service])

  const setUnits = useCallback(
    async (units: Units) => {
      chosenHere.current = true
      setSession((current) => {
        if (!current) {
          return current
        }
        const next: Session = { ...current, user: { ...current.user, units } }
        saveSession(next)
        return next
      })
      try {
        await api.updateUnits(units)
      } catch {
        // Offline — the choice is on this device; the server hears about it next sync.
      }
    },
    [api],
  )

  const setDistanceUnits = useCallback(
    async (distanceUnits: DistanceUnits) => {
      chosenHere.current = true
      setSession((current) => {
        if (!current) {
          return current
        }
        const next: Session = { ...current, user: { ...current.user, distanceUnits } }
        saveSession(next)
        return next
      })
      try {
        await api.updateDistanceUnits(distanceUnits)
      } catch {
        // Offline — the choice is on this device; the server hears about it next sync.
      }
    },
    [api],
  )

  /*
   * Written to the session first and sent afterwards, so the switch answers the finger rather
   * than the connection. A setting that never reaches the account goes back to what it was:
   * there is no queue behind this, so keeping it would leave this device quietly disagreeing
   * with every other one — and the whole point of these living on the account is that they do
   * not do that. Putting it back is also the only way the person finds out.
   */
  const setSettings = useCallback(
    async (settings: Partial<AccountSettings>) => {
      chosenHere.current = true
      /*
       * What these particular keys held, taken inside the write itself rather than off the
       * last render — and only these keys, so a failure puts back what this call changed and
       * nothing else. Read off the render and restored whole, a slow first write failing after
       * a second one succeeded would take the second one down with it.
       *
       * It relies on the updater having run by the time the request settles, which React
       * guarantees for a discrete event — every caller of this is a click or a change. Call it
       * from an effect or a timeout and that no longer holds; the capture fails safe if so,
       * leaving `held` null and skipping the rollback rather than restoring something wrong.
       */
      const before: { held: Partial<AccountSettings> | null } = { held: null }
      setSession((current) => {
        if (!current) {
          return current
        }
        const held = settingsOf(current.user)
        before.held = Object.fromEntries(
          Object.keys(settings).map((key) => [key, held[key as keyof AccountSettings]]),
        )
        const next: Session = {
          ...current,
          user: { ...current.user, settings: { ...held, ...settings } },
        }
        saveSession(next)
        return next
      })
      try {
        await api.updateSettings(settings)
      } catch {
        const back = before.held
        if (back !== null) {
          setSession((current) => {
            if (!current) {
              return current
            }
            const put: Session = {
              ...current,
              user: { ...current.user, settings: { ...settingsOf(current.user), ...back } },
            }
            saveSession(put)
            return put
          })
        }
      }
    },
    [api],
  )

  const deleteAccount = useCallback(async () => {
    await api.deleteAccount()
    clearSession()
    setSession(null)
    await service.reset()
  }, [api, service])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      units: session?.user.units ?? regionDefaultUnits(navigator.language),
      distanceUnits: session?.user.distanceUnits ?? regionDefaultDistanceUnits(navigator.language),
      settings: session === null ? DEFAULT_ACCOUNT_SETTINGS : settingsOf(session.user),
      plan: session?.user.plan ?? null,
      signInWithGoogle,
      signOut,
      setUnits,
      setDistanceUnits,
      setSettings,
      deleteAccount,
    }),
    [session, signInWithGoogle, signOut, setUnits, setDistanceUnits, setSettings, deleteAccount],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error('useAuth needs an AuthProvider above it')
  }
  return value
}
